import algoliasearch from 'algoliasearch/lite';
import React, { useEffect, useState } from 'react';
import {
    Configure,
    connectStateResults,
    Highlight,
    Hits,
    InstantSearch,
    SearchBox,
} from 'react-instantsearch-dom';
import { useNavigate } from 'react-router-dom';
import { enviroment } from '../../enviroment';
import { SearchIcon } from '../siteIcons';
import './SearchBox.css';
import styles from './SearchBox.module.css';

// Initialize Algolia client with error handling
const getSearchClient = () => {
    const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
    const apiKey = process.env.REACT_APP_ALGOLIA_APP_KEY;
    const indexName = process.env.REACT_APP_COMPANY_ID;

    if (!appId || !apiKey || !indexName) {
        console.error('Missing Algolia configuration:', {
            appId: !!appId,
            apiKey: !!apiKey,
            indexName: !!indexName
        });
        return null;
    }

    return algoliasearch(appId, apiKey);
};

const searchClient = getSearchClient();

// Custom SearchBox component
const CustomSearchBox = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (query.trim()) {
            window.location.href = `/search-product/${encodeURIComponent(query.trim())}`;
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.searchForm}>
            <span className={`${styles.searchIcon} position-absolute top-0 bottom-0 m-auto start-0 ms-3 d-inline-flex align-items-center`}>
                <SearchIcon color="#000" />
            </span>
            <SearchBox
                placeholder="Search products..."
                classNames={{
                    root: styles.searchBoxRoot,
                    form: styles.searchBoxForm,
                    input: styles.searchInput,
                    submit: styles.searchBoxSubmit,
                    reset: styles.searchBoxReset,
                }}
                submit={false}
                reset={false}
                onChange={(e) => setQuery(e.target.value)}
                value={query}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(e);
                    }
                }}
            />
        </form>
    );
};

// Custom Hit component
const Hit = ({ hit }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (hit.name_url && hit.product_id) {
            navigate(`/product/${hit.name_url}/${hit.product_id}`);
        }
    };

    return (
        <div className={styles.hitItem} onClick={handleClick}>
            <div className="d-flex align-items-center gap-2">
                {hit.photo && (
                    <img
                        src={hit.photo}
                        alt={hit.name}
                        className={styles.hitImage}
                        width={40}
                        height={40}
                    />
                )}
                <div>
                    <div className={styles.hitName}>
                        <Highlight hit={hit} attribute="name" />
                    </div>
                    {hit.brand && (
                        <div className={styles.hitBrand}>
                            {hit.brand}
                        </div>
                    )}
                    {hit.selling_price && (
                        <div className={styles.hitPrice}>
                            ₹{hit.selling_price}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Results component
const Results = connectStateResults(({ searchState, searchResults, children }) => {
    if (!searchState.query) return null;
    if (searchResults && searchResults.nbHits === 0) {
        return <div className={styles.noResults}>No results found</div>;
    }
    return (
        <div className={styles.hitsContainer}>
            <Hits hitComponent={Hit} />
        </div>
    );
});

export default function SearchBoxComponent({ isMobile = false }) {
    const [isConfigured, setIsConfigured] = useState(false);

    useEffect(() => {
        setIsConfigured(!!searchClient);
    }, []);

    if (!isConfigured) {
        return (
            <div className={`${styles.searchWrapper} ${isMobile ? styles.mobile : ''}`}>
                <form className={styles.searchForm}>
                    <span className={`${styles.searchIcon} position-absolute top-0 bottom-0 m-auto start-0 ms-3 d-inline-flex align-items-center`}>
                        <SearchIcon color="#000" />
                    </span>
                    <input
                        type="search"
                        placeholder="Search products..."
                        className={styles.searchInput}
                        disabled
                    />
                </form>
            </div>
        );
    }

    return (
        <div className={`${styles.searchWrapper} ${isMobile ? styles.mobile : ''}`}>
            <InstantSearch
                searchClient={searchClient}
                indexName={process.env.REACT_APP_COMPANY_ID}
            >
                <Configure
                    filters={`stock > 0 AND store_id = ${enviroment.STORE_ID} AND is_active = 1 AND is_delete = 1`}
                    restrictSearchableAttributes={['name', 'brand', 'category']}
                    attributesToRetrieve={['product_id', 'id', 'photo', 'stock', 'is_active', 'is_delete', 'brand', 'name', 'color', 'mrp', 'selling_price', 'category', 'name_url', 'age', 'gender']}
                    attributesToHighlight={['name', 'brand', 'category']}
                    typoTolerance="min"
                    hitsPerPage={30}
                    facets={['color', 'brand', 'selling_price', 'category']}
                    distinct={true}
                    advancedSyntax={true}
                    removeStopWords={true}
                    queryType="prefixLast"
                    minWordSizefor1Typo={4}
                    minWordSizefor2Typos={8}
                    minProximity={2}
                    ranking={['exact', 'typo', 'words', 'proximity', 'attribute', 'filters', 'custom']}
                    removeWordsIfNoResults="allOptional"
                    exactOnSingleWordQuery="attribute"
                    alternativesAsExact={['ignorePlurals', 'singleWordSynonym']}
                    ignorePlurals={true}
                    queryLanguages={['en']}
                />
                <CustomSearchBox />
                <Results />
            </InstantSearch>
        </div>
    );
};